'use client';

/**
 * UnifiedHeader - Single Header Component for All Page Types
 *
 * Variants:
 * - full: Staff dashboard with navigation, search, notifications
 * - conference: Compact header for video call pages with duration/recording
 * - citizen: Citizen portal with emerald branding and emergency bar
 * - developer: Developer portal with violet branding and dev links
 * - minimal: Auth pages with logo only and optional back button
 */

import React, { useState, useRef, useEffect } from 'react';
import Link from 'next/link';
import { usePathname, useRouter } from 'next/navigation';
import {
  ArrowLeft,
  Phone,
  Clock,
  Circle,
  Search,
  Bell,
  User,
  LogOut,
  Settings,
  ChevronDown,
  X,
  FileText,
  Mic,
  Code2,
  FileJson,
  Key,
  LogIn,
  Rocket,
  Shield,
  Terminal,
  UserPlus,
  Drama,
  Layers,
  FlaskConical,
  Bug,
} from 'lucide-react';
import {
  useAppShell,
  useAgencyBranding,
  useIsStaff,
  useIsCitizen,
  useIsAdmin,
  useHasPermission,
  useAdminStatus,
} from '../AppShellContext';
import { useMasquerade } from '@/hooks/useMasquerade';
import { MasqueradeDialog } from '../MasqueradeDialog';
import { useTierSimulator } from '@/hooks/useTierSimulator';
import { TestingConsoleDialog } from '../TestingConsoleDialog';
import { HeaderCallInfo } from '@/components/ui/HeaderCallInfo';
import { NavBreadcrumb } from '../navigation/NavBreadcrumb';
import { useHeaderActionsState } from './HeaderActionsContext';
import { HeaderActionsRenderer } from './HeaderActionButton';
import { clearAuthLocalStorage } from '@/lib/auth/logout';
import { collectHeldRoles, formatHeldRoles, LOADING_ROLES_LABEL, ROLES_UNAVAILABLE_LABEL } from '@/lib/auth/held-roles';
import { InlineVisibilityControls } from './InlineVisibilityControls';
import { VISIBILITY_SIMPLIFICATION_ENABLED } from '@/lib/visibility';
import { NotificationCenter } from '@/components/notifications';
import { SearchAizWithProvider } from '@/components/search-aiz';
import logger from '@/lib/logger';
import { InstantCallButton } from './InstantCallButton';
import { resolveRunbook, useRunbook } from '@/lib/runbook/client';
import { RunbookModeSwitcher } from '@/components/shell/RunbookModeSwitcher';
import { useAgentDebug } from '@/contexts/AgentDebugContext';
import { PrimaryBrandLogo, PRODUCT_NAME } from '@/components/brand/EngageIQBrand';
import { callIdFromPath, inCallSearchReceiverFor, MIN_IN_CALL_QUERY_CHARS, requestInCallSearch } from '@/lib/search/inCallSearch';
import { useInCallSearchCallId } from '@/lib/search/useInCallSearchReceiver';

// ============================================================================
// Types
// ============================================================================

export type HeaderVariant = 'full' | 'conference' | 'citizen' | 'developer' | 'minimal';

export interface UnifiedHeaderProps {
  /** Header variant determines which features are shown */
  variant?: HeaderVariant;

  /** Custom className */
  className?: string;

  // ─── Conference-specific props ───────────────────────────────────────────
  /** Call duration in seconds */
  duration?: number;

  /** Whether recording is active */
  isRecording?: boolean;

  // ─── Citizen-specific props ──────────────────────────────────────────────
  /** Show emergency bar (citizen variant) */
  showEmergency?: boolean;

  /** Emergency phone number (citizen variant) */
  emergencyPhone?: string;

  // ─── Minimal-specific props ──────────────────────────────────────────────
  /** Center the logo (minimal variant) */
  centered?: boolean;

  /** Show back link (minimal/conference variant) */
  showBack?: boolean;

  /** Back link destination */
  backHref?: string;

  /** Back link label (minimal variant) */
  backLabel?: string;

  /** Show logo (minimal variant, default: true) */
  showLogo?: boolean;

  // ─── Full-specific props ─────────────────────────────────────────────────
  /** Show navigation (full variant) */
  showNav?: boolean;

  /** Show call info (full variant) */
  showCallInfo?: boolean;

  /** Custom right slot content */
  rightSlot?: React.ReactNode;

  /** Branding override (usually from context) */
  branding?: any;
}

// ============================================================================
// Color Schemes
// ============================================================================

const colorSchemes = {
  blue: {
    activeBg: 'bg-blue-500/10',
    activeText: 'text-blue-400',
    borderColor: 'border-blue-500/20',
    iconBg: 'bg-blue-500/10',
    iconColor: 'text-blue-400',
  },
  amber: {
    activeBg: 'bg-amber-500/10',
    activeText: 'text-amber-400',
    borderColor: 'border-amber-500/20',
    iconBg: 'bg-amber-500/10',
    iconColor: 'text-amber-400',
  },
  emerald: {
    activeBg: 'bg-emerald-500/10',
    activeText: 'text-emerald-400',
    borderColor: 'border-emerald-500/20',
    iconBg: 'bg-emerald-500/10',
    iconColor: 'text-emerald-400',
  },
  cyan: {
    activeBg: 'bg-cyan-500/10',
    activeText: 'text-cyan-400',
    borderColor: 'border-cyan-500/20',
    iconBg: 'bg-cyan-500/10',
    iconColor: 'text-cyan-400',
  },
  purple: {
    activeBg: 'bg-purple-500/10',
    activeText: 'text-purple-400',
    borderColor: 'border-purple-500/20',
    iconBg: 'bg-purple-500/10',
    iconColor: 'text-purple-400',
  },
  violet: {
    activeBg: 'bg-violet-500/10',
    activeText: 'text-violet-400',
    borderColor: 'border-violet-500/20',
    iconBg: 'bg-violet-500/10',
    iconColor: 'text-violet-400',
  },
  rose: {
    activeBg: 'bg-blue-500/10',
    activeText: 'text-blue-400',
    borderColor: 'border-blue-500/20',
    iconBg: 'bg-blue-500/10',
    iconColor: 'text-blue-400',
  },
};

type ColorScheme = keyof typeof colorSchemes;

// ============================================================================
// Main Component
// ============================================================================

export function UnifiedHeader({
  variant = 'full',
  className = '',
  // Conference props
  duration,
  isRecording = false,
  // Citizen props
  showEmergency = true,
  emergencyPhone = resolveRunbook('ui-frontend.shell.header.emergency_phone', '911'),
  // Minimal props
  centered = false,
  showBack = false,
  backHref = '/',
  backLabel = 'Back',
  showLogo = true,
  // Full props
  showNav = true,
  showCallInfo = true,
  rightSlot,
}: UnifiedHeaderProps) {
  const pathname = usePathname();
  const router = useRouter();
  const { user, currentRoute } = useAppShell();
  const branding = useAgencyBranding();
  const isStaff = useIsStaff();
  const isCitizen = useIsCitizen();
  const pageActions = useHeaderActionsState();

  // Debug log pageActions for diagnosing visibility issues
  useEffect(() => {
    if (variant === 'conference') {
      logger.debug('UnifiedHeader', 'Conference header pageActions', {
        variant,
        pageActionsCount: pageActions.length,
        pageActionIds: pageActions.map(a => a.id),
        isStaff,
        pathname,
      });
    }
  }, [variant, pageActions, isStaff, pathname]);

  // Runbook overrides — useRunbook handles SSR/hydration correctly
  const rbSecureCitizenPortal = useRunbook('ui-frontend.shell.header.secure_citizen_portal', 'Secure Citizen Portal');
  const rbEmergencyLabel = useRunbook('ui-frontend.shell.header.emergency_label', 'Emergency?');
  const rbCallPrefix = useRunbook('ui-frontend.shell.header.call_emergency_prefix', 'Call');
  const rbCitizenPortalLabel = useRunbook('ui-frontend.shell.header.citizen_portal_label', 'Citizen Portal');

  // State
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchFocused, setSearchFocused] = useState(false);

  // Refs
  const userMenuRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLDivElement>(null);

  // Close menus on click outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target as Node)) {
        setUserMenuOpen(false);
      }
      if (searchRef.current && !searchRef.current.contains(event.target as Node)) {
        setSearchFocused(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const inCallSearchCallId = useInCallSearchCallId(pathname);
  const onCallPage = callIdFromPath(pathname) !== null;
  const [searchNotice, setSearchNotice] = useState<string | null>(null);

  // Search must not unmount a joined call. Before join, use a separate tab.
  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const query = searchQuery.trim();
    if (!query) return;
    setSearchNotice(null);
    const callId = inCallSearchReceiverFor(pathname);
    if (callId) {
      if (requestInCallSearch(callId, query)) setSearchFocused(false);
      return;
    }
    const href = `/search?q=${encodeURIComponent(query)}`;
    if (onCallPage) {
      // noopener in window.open's features returns null even on success. Clear
      // opener after opening so a blocked popup can be reported honestly.
      const tab = window.open(href, '_blank');
      if (!tab) {
        setSearchNotice('Your browser blocked the new tab. Allow pop-ups for this site, or join the call and search here.');
        return;
      }
      tab.opener = null;
      setSearchFocused(false);
      return;
    }
    window.location.href = href;
  };

  // Handle keyboard shortcut (Cmd/Ctrl + K) - only for conference variant
  // Note: Full variant uses SearchAiz which has its own ⌘K handling via useCommandPalette
  useEffect(() => {
    if (variant !== 'conference') return;

    function handleKeyDown(event: KeyboardEvent) {
      if ((event.metaKey || event.ctrlKey) && event.key === 'k') {
        event.preventDefault();
        const searchInput = document.getElementById('global-search-input');
        if (searchInput) {
          searchInput.focus();
        }
      }
    }
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [variant]);

  // Common values
  const customLogoUrl = branding?.logo_dark_url || branding?.logo_url;
  const brandAlt = branding?.company_name || PRODUCT_NAME;
  const userInitials = user?.email?.substring(0, 2).toUpperCase() || 'U';

  // ─── Minimal Variant ─────────────────────────────────────────────────────
  if (variant === 'minimal') {
    return (
      <header
        className={`px-4 py-3 ${className}`}
        style={{ backgroundColor: branding?.dark_background_color || 'transparent' }}
      >
        <div className={`flex items-center ${centered ? 'justify-center' : 'justify-between'}`}>
          {/* Back Link */}
          {showBack && !centered && (
            <Link
              href={backHref}
              className="flex items-center gap-2 text-sm text-slate-400 hover:text-white transition"
            >
              <ArrowLeft className="w-4 h-4" />
              <span>{backLabel}</span>
            </Link>
          )}

          {/* Logo */}
          {showLogo && (
            <Link
              href="/"
              className="flex-shrink-0 hover:opacity-90 transition focus:outline-none focus:ring-2 focus:ring-blue-500 rounded"
            >
              <PrimaryBrandLogo
                customSrc={customLogoUrl}
                customAlt={brandAlt}
                theme="dark"
                width={140}
                height={38}
                style={{ width: 'auto', height: 'auto', maxHeight: '28px' }}
              />
            </Link>
          )}

          {/* Spacer for alignment */}
          {showBack && !centered && showLogo && <div className="w-16" />}
        </div>
      </header>
    );
  }

  // ─── Citizen Variant ─────────────────────────────────────────────────────
  if (variant === 'citizen') {
    return (
      <header
        className={`sticky top-0 z-50 border-b border-emerald-500/30 ${className}`}
        style={{ backgroundColor: branding?.dark_background_color || 'rgba(6, 78, 59, 0.95)' }}
      >
        {/* Emergency Bar */}
        {showEmergency && (
          <div className="px-4 py-2 bg-emerald-950/50 border-b border-emerald-900/20">
            <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-1 sm:gap-0 text-sm">
              <div className="flex items-center gap-2 text-emerald-400">
                <Shield className="w-4 h-4" />
                <span className="font-medium hidden sm:inline">{rbSecureCitizenPortal}</span>
              </div>
              <div className="flex items-center gap-3 text-slate-400">
                <span>{rbEmergencyLabel}</span>
                <a
                  href={`tel:${emergencyPhone}`}
                  className="flex items-center gap-1.5 px-3 py-1 bg-red-500/20 text-red-400 hover:bg-red-500/30 hover:text-red-300 font-medium rounded-full transition"
                >
                  <Phone className="w-4 h-4" />
                  <span>{rbCallPrefix} {emergencyPhone}</span>
                </a>
              </div>
            </div>
          </div>
        )}

        {/* Main Header */}
        <div className="px-4 py-3">
          <div className="max-w-7xl mx-auto flex items-center justify-between gap-3">
            {/* Left: Logo + Portal Name */}
            <div className="flex items-center gap-4 min-w-0">
              <Link href="/citizen" className="flex-shrink-0 hover:opacity-90 transition">
                <PrimaryBrandLogo
                  customSrc={customLogoUrl}
                  customAlt={brandAlt}
                  theme="dark"
                  width={140}
                  height={38}
                  style={{ width: 'auto', height: 'auto', maxHeight: '28px' }}
                />
              </Link>
              {/* Citizen Portal label — hidden on mobile, shown on large screens only */}
              <div className="hidden lg:flex items-center gap-2">
                <div className="w-px h-6 bg-slate-700" />
                <span className="text-emerald-400 font-medium text-sm">{rbCitizenPortalLabel}</span>
              </div>
            </div>

            {/* Right: Auth */}
            <div className="flex items-center gap-2 sm:gap-3 sm:flex-shrink-0 overflow-hidden">
              {user ? (
                <div className="flex items-center gap-3">
                  <div className="hidden sm:flex items-center gap-2 px-3 py-1.5 bg-emerald-500/10 border border-emerald-500/30 rounded-full">
                    <div className="w-2 h-2 bg-emerald-400 rounded-full animate-pulse" />
                    <span className="text-sm text-emerald-300 font-medium">Welcome back</span>
                  </div>
                  <UserMenuDropdown
                    user={user}
                    userInitials={userInitials}
                    isOpen={userMenuOpen}
                    setIsOpen={setUserMenuOpen}
                    menuRef={userMenuRef}
                  />
                  <form
                    action="/auth/signout"
                    method="post"
                    onSubmit={() => clearAuthLocalStorage()}
                    className="hidden lg:block"
                  >
                    <button
                      type="submit"
                      className="text-sm text-slate-400 hover:text-slate-200 transition underline underline-offset-2"
                    >
                      Not you?
                    </button>
                  </form>
                </div>
              ) : (
                <div className="flex items-center gap-2 sm:gap-3">
                  <Link
                    href="/citizen/login"
                    className="flex items-center gap-2 px-2.5 sm:px-4 py-2 text-sm text-slate-200 hover:text-white border border-slate-600 hover:border-slate-500 rounded-lg transition"
                  >
                    <LogIn className="w-5 h-5" />
                    <span className="hidden sm:inline">Sign In</span>
                  </Link>
                  <Link
                    href="/citizen/register"
                    className="flex items-center gap-2 px-2.5 sm:px-4 py-2 text-sm bg-emerald-600 hover:bg-emerald-500 text-white font-medium rounded-lg transition"
                  >
                    <UserPlus className="w-5 h-5" />
                    <span className="hidden sm:inline">Register</span>
                  </Link>
                </div>
              )}
            </div>
          </div>
        </div>
      </header>
    );
  }

  // ─── Developer Variant ───────────────────────────────────────────────────
  if (variant === 'developer') {
    return (
      <header
        className={`border-b border-violet-500/30 ${className}`}
        style={{ backgroundColor: branding?.dark_background_color || 'rgba(46, 16, 101, 0.95)' }}
      >
        {/* Developer Info Bar */}
        <div className="px-4 py-2 bg-violet-950/50 border-b border-violet-900/20">
          <div className="max-w-7xl mx-auto flex items-center justify-between text-sm">
            <div className="flex items-center gap-4">
              <Link
                href="/"
                className="flex items-center gap-1.5 text-slate-400 hover:text-violet-300 transition"
              >
                <ArrowLeft className="w-4 h-4" />
                <span>Back to Home</span>
              </Link>
              <div className="w-px h-4 bg-violet-800" />
              <div className="flex items-center gap-2 text-violet-400">
                <Code2 className="w-4 h-4" />
                <span className="font-medium">Developer Portal</span>
              </div>
            </div>
            <div className="flex items-center gap-4 text-slate-400">
              <a
                href="https://docs.neutralaiz.com"
                target="_blank"
                rel="noopener noreferrer"
                className="hover:text-violet-300 transition"
              >
                Documentation
              </a>
              <a
                href="https://status.neutralaiz.com"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-1 hover:text-violet-300 transition"
              >
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                <span>Status</span>
              </a>
            </div>
          </div>
        </div>

        {/* Main Header */}
        <div className="px-4 py-3">
          <div className="max-w-7xl mx-auto flex items-center justify-between">
            {/* Left: Logo + Portal Name */}
            <div className="flex items-center gap-4">
              <Link href="/developers" className="flex-shrink-0 hover:opacity-90 transition">
                <PrimaryBrandLogo
                  customSrc={customLogoUrl}
                  customAlt={brandAlt}
                  theme="dark"
                  width={140}
                  height={38}
                  style={{ width: 'auto', height: 'auto', maxHeight: '28px' }}
                />
              </Link>
              <div className="hidden sm:flex items-center gap-2">
                <div className="w-px h-6 bg-slate-700" />
                <span className="text-violet-400 font-medium text-sm">Developer Portal</span>
              </div>
            </div>

            {/* Center: Navigation - API Docs and Example App require login */}
            <nav className="hidden md:flex items-center gap-1">
              {user ? (
                <>
                  <DeveloperNavLink href="/api-docs" icon={FileJson}>API Docs</DeveloperNavLink>
                  {/* API Keys page hidden - not ready for public use */}
                  {/* <DeveloperNavLink href="/developers/keys" icon={Key}>API Keys</DeveloperNavLink> */}
                  <DeveloperNavLink href="/ai-demo" icon={Terminal}>Example App</DeveloperNavLink>
                </>
              ) : (
                <span className="text-sm text-slate-500 italic">Sign in to access developer tools</span>
              )}
            </nav>

            {/* Right: Auth */}
            <div className="flex items-center gap-3">
              {user ? (
                <UserMenuDropdown
                  user={user}
                  userInitials={userInitials}
                  isOpen={userMenuOpen}
                  setIsOpen={setUserMenuOpen}
                  menuRef={userMenuRef}
                />
              ) : (
                <div className="flex items-center gap-2">
                  <Link
                    href="/login"
                    className="flex items-center gap-2 px-3 py-1.5 text-sm text-slate-300 hover:text-white transition"
                  >
                    <LogIn className="w-4 h-4" />
                    <span className="hidden sm:inline">Sign In</span>
                  </Link>
                  <Link
                    href="/developers/register"
                    className="flex items-center gap-2 px-4 py-1.5 text-sm bg-violet-600 hover:bg-violet-500 text-white rounded-lg transition"
                  >
                    <Rocket className="w-4 h-4" />
                    <span>Get Started</span>
                  </Link>
                </div>
              )}
            </div>
          </div>
        </div>
      </header>
    );
  }

  // ─── Conference Variant ──────────────────────────────────────────────────
  if (variant === 'conference') {
    return (
      <header
        className={`flex items-center justify-between px-2 lg:px-4 border-b border-white/[0.08] ${className}`}
        style={{ height: '64px', backgroundColor: '#000', zIndex: 100 }}
      >
        {/* Left Side: Logo + Breadcrumbs */}
        <div className="flex items-center gap-3 lg:gap-6 flex-nowrap overflow-hidden min-w-0 flex-1">
          {/* Back Button */}
          {showBack && (
            <Link
              href={backHref}
              className="flex items-center justify-center w-8 h-8 rounded-lg bg-white/[0.06] hover:bg-white/[0.1] transition text-white/60 hover:text-white"
            >
              <ArrowLeft className="w-4 h-4" />
            </Link>
          )}

          {/* Logo */}
          <Link href={user ? '/dashboard' : '/'} className="flex-shrink-0 hover:opacity-90 transition">
            <PrimaryBrandLogo
              customSrc={customLogoUrl}
              customAlt={brandAlt}
              theme="dark"
              width={140}
              height={38}
              priority
              style={{ width: 'auto', height: 'auto', maxHeight: '32px' }}
            />
          </Link>

          {/* Breadcrumbs */}
          {isStaff && <NavBreadcrumb className="hidden md:flex flex-1" />}
        </div>

        {/* Right Side */}
        <div className="flex items-center gap-1.5 lg:gap-3 flex-shrink-0">
          {/* Call Info */}
          <HeaderCallInfo />

          {/* Recording Indicator */}
          {isRecording && (
            <div className="flex items-center gap-2 px-3 py-1 rounded-full bg-blue-500/20 border border-blue-500/30">
              <Circle className="w-3 h-3 text-blue-500 fill-blue-500 animate-pulse" />
              <span className="text-xs font-medium text-blue-400">REC</span>
            </div>
          )}

          {/* Duration */}
          {duration !== undefined && (
            <div className="flex items-center gap-2 px-3 py-1 rounded-lg bg-white/[0.04] text-sm">
              <Clock className="w-4 h-4 text-white/70" />
              <span className="font-mono text-white">{formatDuration(duration)}</span>
            </div>
          )}

          {/* Separator — hidden on mobile to avoid overlapping wizard tab buttons */}
          <div className="hidden sm:block w-px h-6 bg-white/[0.12]" />

          {/* Page Actions */}
          {pageActions.length > 0 && (
            <>
              <HeaderActionsRenderer actions={pageActions} />
              <div className="hidden sm:block w-px h-6 bg-white/[0.12]" />
            </>
          )}

          {/* Instant Call — same affordance as the dashboard/Full header. The
              conference header variant is also used on incident-report (after-call),
              where a "Start Instant Call" was previously missing. Hidden during
              active calls (this variant also backs /calls/[callId]). */}
          {isStaff && currentRoute?.path !== '/calls/[callId]' && (
            <>
              <InstantCallButton />
              <div className="hidden sm:block w-px h-6 bg-white/[0.12]" />
            </>
          )}

          {/* Search Bar (compact) — hidden below 2xl (1536px) to give breadcrumbs room;
              users can always press Ctrl+K to search */}
          {isStaff && (
            <div ref={searchRef} className="hidden 2xl:block">
              <form onSubmit={handleSearchSubmit} className="relative">
                <div className={`
                  flex items-center gap-2 px-3 py-1.5 rounded-lg border transition-all duration-200
                  ${searchFocused
                    ? 'bg-cyan-500/10 border-cyan-500/30 ring-1 ring-cyan-500/20'
                    : 'bg-white/[0.04] border-white/[0.08] hover:border-white/[0.15]'
                  }
                `}>
                  <Search className={`w-4 h-4 transition-colors duration-200 ${searchFocused ? 'text-cyan-400' : 'text-white/70'}`} />
                  <input
                    id="global-search-input"
                    type="text"
                    placeholder="Search..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    onFocus={() => setSearchFocused(true)}
                    className="w-24 lg:w-32 bg-transparent text-sm text-white placeholder-white/40 focus:outline-none"
                    autoComplete="off"
                  />
                  {searchQuery && (
                    <button
                      type="button"
                      onClick={() => setSearchQuery('')}
                      className="p-0.5 rounded hover:bg-white/[0.1] text-white/70 hover:text-white transition-colors"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  )}
                </div>

                {/* Search Suggestions */}
                {searchFocused && searchQuery && (
                  <SearchSuggestionsDropdown
                    searchQuery={searchQuery}
                    onSelect={() => setSearchFocused(false)}
                    inCall={inCallSearchCallId !== null}
                    newTab={onCallPage}
                    notice={searchNotice}
                  />
                )}
              </form>
            </div>
          )}

          {/* Custom Right Slot */}
          {rightSlot}

          {/* Notifications (compact) */}
          {user && <NotificationCenter compact />}

          {/* User Menu */}
          {user ? (
            <UserMenuDropdown
              user={user}
              userInitials={userInitials}
              isOpen={userMenuOpen}
              setIsOpen={setUserMenuOpen}
              menuRef={userMenuRef}
              compact
            />
          ) : (
            <Link
              href="/login"
              className="hidden lg:inline-flex px-3 py-1.5 text-sm font-medium text-white bg-white/10 hover:bg-white/15 rounded-lg transition-colors"
            >
              Sign In
            </Link>
          )}
        </div>
      </header>
    );
  }

  // ─── Full Variant (Default) ──────────────────────────────────────────────
  return (
    <header className={`sticky top-0 z-50 ${className}`} style={{ backgroundColor: '#000' }}>
      <div className="h-16 px-6 flex items-center justify-between border-b border-white/[0.08]">
        {/* Logo */}
        <div className="flex items-center gap-10">
          <Link
            href={user ? '/dashboard' : '/'}
            data-demo-id="header-logo"
            className="opacity-90 hover:opacity-100 transition-opacity"
          >
            <PrimaryBrandLogo
              customSrc={customLogoUrl}
              customAlt={brandAlt}
              theme="dark"
              width={160}
              height={44}
              priority
              style={{ width: 'auto', height: 'auto', maxHeight: '36px' }}
            />
          </Link>

          {/* Nav Breadcrumb */}
          {showNav && isStaff && <NavBreadcrumb className="hidden md:flex" />}
        </div>

        {/* Right Side */}
        <div className="flex items-center gap-3">
          {/* Global Search Bar - SearchAiz
              Hidden below 2xl (1536px) to give breadcrumbs room — matches the
              conference variant. Users can always press Ctrl+K to search. */}
          {showNav && isStaff && (
            <div className="hidden 2xl:block">
              <SearchAizWithProvider
                context="header"
                placeholder="Search..."
                onResultSelect={(result) => {
                  // Only navigate to specific result pages, not /search pages
                  // The InlineSearch component handles navigation internally
                  // This callback is for additional handling if needed
                  logger.debug('UnifiedHeader', 'Search result selected', {
                    id: result.id,
                    type: result.type,
                    url: result.url
                  });
                }}
              />
            </div>
          )}

          {/* Call Info */}
          {showCallInfo && currentRoute?.layout === 'conference' && <HeaderCallInfo />}

          {/* Instant Call Button (split-button with recent calls dropdown) */}
          {/* Hidden only during active calls — visible on incident report, playback, dispatch, etc. */}
          {isStaff && currentRoute?.path !== '/calls/[callId]' && (
            <div className="mr-2">
              <InstantCallButton />
            </div>
          )}

          {/* Runbook Mode Switcher (admin-only — switches demo scenario instantly) */}
          <RunbookModeSwitcher />

          {/* Notifications */}
          {user && <NotificationCenter />}

          {/* User Menu */}
          {user ? (
            <UserMenuDropdown
              user={user}
              userInitials={userInitials}
              isOpen={userMenuOpen}
              setIsOpen={setUserMenuOpen}
              menuRef={userMenuRef}
            />
          ) : (
            <Link
              href="/login"
              className="px-4 py-2 text-sm font-medium text-white bg-white/10 hover:bg-white/15 rounded-lg transition-colors"
            >
              Sign In
            </Link>
          )}
        </div>
      </div>
    </header>
  );
}

// ============================================================================
// Sub-components
// ============================================================================

// ─── User Menu Dropdown ────────────────────────────────────────────────────

interface UserMenuDropdownProps {
  user: any;
  userInitials: string;
  isOpen: boolean;
  setIsOpen: (open: boolean) => void;
  menuRef: React.RefObject<HTMLDivElement | null>;
  compact?: boolean;
}

export function UserMenuDropdown({
  user,
  userInitials,
  isOpen,
  setIsOpen,
  menuRef,
  compact = false,
}: UserMenuDropdownProps) {
  const { accessControlState } = useAppShell();
  const rolesLabel = accessControlState.error
    ? ROLES_UNAVAILABLE_LABEL
    : accessControlState.loading
      ? LOADING_ROLES_LABEL
      : accessControlState.userContext
        ? formatHeldRoles(collectHeldRoles(accessControlState.userContext))
        : ROLES_UNAVAILABLE_LABEL;
  const menuPathname = usePathname();
  const isAdmin = useIsAdmin();
  const adminStatus = useAdminStatus();
  const isCitizenUser = useIsCitizen();
  const { canMasquerade, isActive: isMasquerading, roleNames: masqueradeRoleNames, deactivateMasquerade } = useMasquerade();
  const canLayoutVisibility = useHasPermission('layout.visibility');
  const [masqueradeDialogOpen, setMasqueradeDialogOpen] = useState(false);
  const [testingConsoleOpen, setTestingConsoleOpen] = useState(false);
  const { canSimulate: canSimulateTier, isSimulating: isTierSimulating } = useTierSimulator();
  // Agent Tools & Thoughts — platform admins only, and only when there's an
  // active call to inspect (binder on the call page sets the debug callId).
  const { open: openAgentDebug, callId: agentDebugCallId } = useAgentDebug();

  return (
    <div ref={menuRef} className="relative">
      <button
        onClick={() => setIsOpen(!isOpen)}
        data-demo-id="header-user-menu-btn"
        className={`flex items-center gap-2 rounded-lg hover:bg-white/[0.06] transition-colors group ${
          compact ? 'p-1.5 pr-2' : 'p-1.5 pr-3'
        }`}
      >
        <div className={`rounded-lg bg-gradient-to-br ${
          isMasquerading ? 'from-amber-500 to-orange-600 ring-amber-500/30' : 'from-blue-500 to-indigo-600 ring-white/10'
        } flex items-center justify-center font-semibold text-white shadow-lg ${
          isMasquerading ? 'shadow-amber-500/20' : 'shadow-blue-500/20'
        } ring-2 group-hover:ring-white/20 transition-all ${
          compact ? 'w-8 h-8 text-xs' : 'w-9 h-9 text-sm'
        }`}>
          {isMasquerading ? <Drama className="w-4 h-4" /> : userInitials}
        </div>
        <ChevronDown className={`w-4 h-4 text-white/70 group-hover:text-slate-300 transition-all duration-200 ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {/* Dropdown */}
      {isOpen && (
        <div className={`absolute right-0 mt-2 py-1.5 bg-[#0a0a0a] border border-blue-500/20 rounded-xl shadow-2xl z-50 ${
          compact ? 'w-64' : 'w-72'
        }`}>
          {/* User Info */}
          <div className="px-4 py-3 border-b border-white/[0.06]">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center text-sm font-semibold text-white">
                {userInitials}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-white truncate">{user.email}</p>
                <p className="text-xs text-white/70 mt-0.5">
                  {rolesLabel}
                </p>
              </div>
            </div>
          </div>

          {/* Menu Items */}
          <div className="py-1.5 px-1.5">
            {!isCitizenUser && (
              <UserMenuLink
                href="/profile"
                icon={User}
                label="Profile"
                description="View your profile"
                color="cyan"
                dataDemoId="header-menu-profile"
                onClick={() => setIsOpen(false)}
              />
            )}
            {isAdmin && !menuPathname?.startsWith('/calls/') && (
              <UserMenuLink
                href="/settings"
                icon={Settings}
                label="Settings"
                description="Manage preferences"
                color="purple"
                dataDemoId="header-menu-settings"
                onClick={() => setIsOpen(false)}
              />
            )}

            {/* Testing Console — unified masquerade + tier simulator */}
            {(canMasquerade || canSimulateTier) && (
              <button
                onClick={() => {
                  setIsOpen(false);
                  setTestingConsoleOpen(true);
                }}
                className="w-full flex items-start gap-3 px-3 py-2.5 rounded-lg text-sm text-white/70 hover:text-white hover:bg-white/[0.04] transition-colors group"
              >
                <div className={`p-1.5 rounded transition-colors ${
                  isMasquerading || isTierSimulating
                    ? 'bg-cyan-500/20'
                    : 'bg-cyan-500/10 group-hover:bg-cyan-500/20'
                }`}>
                  <FlaskConical className={`w-4 h-4 transition-colors ${
                    isMasquerading || isTierSimulating ? 'text-cyan-400' : 'text-cyan-400/70'
                  }`} />
                </div>
                <div className="flex-1 min-w-0">
                  <p className={`font-medium transition-colors ${
                    isMasquerading || isTierSimulating ? 'text-cyan-300' : 'text-white/80 group-hover:text-white'
                  }`}>
                    Testing Console
                  </p>
                  <p className="text-xs text-white/70 mt-0.5">
                    {isMasquerading && isTierSimulating
                      ? `Role: ${masqueradeRoleNames.join(', ')} · Tier: simulated`
                      : isMasquerading
                      ? `Masquerading as: ${masqueradeRoleNames.join(', ')}`
                      : isTierSimulating
                      ? 'Simulating license tier'
                      : 'Test as different roles and tiers'}
                  </p>
                </div>
              </button>
            )}

            {/* Agent Tools & Thoughts — platform admins, only on a call page
                (binder sets the debug callId when /calls/[callId] mounts).
                NOTE: A duplicate of this item was added to UserMenu.tsx by a
                parallel session, but UserMenu.tsx isn't the rendered menu —
                UnifiedHeader.tsx is. The item BELONGS here. */}
            {adminStatus.isPlatformAdmin && agentDebugCallId && (
              <button
                onClick={() => {
                  setIsOpen(false);
                  openAgentDebug();
                }}
                className="w-full flex items-start gap-3 px-3 py-2.5 rounded-lg text-sm text-white/70 hover:text-white hover:bg-white/[0.04] transition-colors group"
                data-demo-id="header-menu-agent-debug"
              >
                <div className="p-1.5 rounded bg-amber-500/10 group-hover:bg-amber-500/20 transition-colors">
                  <Bug className="w-4 h-4 text-amber-400/80 group-hover:text-amber-400 transition-colors" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-white/80 group-hover:text-white transition-colors">
                    Agent Tools &amp; Thoughts
                  </p>
                  <p className="text-xs text-white/70 mt-0.5">
                    Live debug feeds for this call (admin only)
                  </p>
                </div>
              </button>
            )}
          </div>

          {/* Call Page Layout Visibility — permission-gated; hidden entirely while the
              UI-simplification feature is turned off (it would be a no-op). */}
          {VISIBILITY_SIMPLIFICATION_ENABLED && canLayoutVisibility && <InlineVisibilityControls />}

          {/* Sign Out */}
          <div className="border-t border-white/[0.06] pt-1.5 px-1.5 pb-0.5">
            <form
              action="/auth/signout"
              method="post"
              onSubmit={() => {
                // CRITICAL: Clear localStorage BEFORE form submits to prevent stale role data
                clearAuthLocalStorage();
              }}
            >
              <button
                type="submit"
                data-demo-id="header-menu-signout"
                className="w-full flex items-center gap-3 px-3 py-2.5 text-sm rounded-lg text-white/60 hover:text-blue-400 hover:bg-blue-500/10 transition-colors group"
              >
                <div className="p-1.5 rounded bg-white/[0.04] group-hover:bg-blue-500/10 transition-colors">
                  <LogOut className="w-4 h-4 text-white/70 group-hover:text-blue-400 transition-colors" />
                </div>
                <span>Sign Out</span>
              </button>
            </form>
          </div>
        </div>
      )}

      {/* Masquerade Role Selector Dialog (standalone — kept for direct access) */}
      <MasqueradeDialog
        open={masqueradeDialogOpen}
        onOpenChange={setMasqueradeDialogOpen}
      />

      {/* Unified Testing Console — roles + tiers in one dialog */}
      <TestingConsoleDialog
        open={testingConsoleOpen}
        onOpenChange={setTestingConsoleOpen}
      />
    </div>
  );
}

// ─── User Menu Link ────────────────────────────────────────────────────────

interface UserMenuLinkProps {
  href: string;
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  description?: string;
  color?: ColorScheme;
  dataDemoId?: string;
  onClick?: () => void;
}

function UserMenuLink({ href, icon: Icon, label, description, color = 'blue', dataDemoId, onClick }: UserMenuLinkProps) {
  const colors = colorSchemes[color];

  return (
    <Link
      href={href}
      onClick={onClick}
      data-demo-id={dataDemoId}
      className="flex items-start gap-3 px-3 py-2.5 rounded-lg text-sm text-white/70 hover:text-white hover:bg-white/[0.04] transition-colors group"
    >
      <div className={`p-1.5 rounded ${colors.iconBg} group-hover:bg-opacity-20 transition-colors`}>
        <Icon className={`w-4 h-4 ${colors.iconColor} transition-colors`} />
      </div>
      <div className="flex-1 min-w-0">
        <p className="font-medium text-white/80 group-hover:text-white transition-colors">{label}</p>
        {description && <p className="text-xs text-white/70 mt-0.5">{description}</p>}
      </div>
    </Link>
  );
}

// ─── Search Suggestions Dropdown ───────────────────────────────────────────

interface SearchSuggestionsDropdownProps {
  searchQuery: string;
  onSelect: () => void;
  /** In a joined call: search in the call instead of offering links that leave it. */
  inCall?: boolean;
  /** On the call page before it is joined: open the links in a new tab, never over the call. */
  newTab?: boolean;
  /** Why the last search did not open (a blocked new tab). */
  notice?: string | null;
}

function SearchSuggestionsDropdown({ searchQuery, onSelect, inCall = false, newTab = false, notice = null }: SearchSuggestionsDropdownProps) {
  const noticeRow = notice ? (
    <p className="text-xs text-amber-300 px-4 py-2 border-b border-white/[0.06]" data-testid="header-search-notice" role="status">{notice}</p>
  ) : null;
  if (inCall) {
    return (
      <div className="absolute right-0 top-full mt-1 w-80 bg-[#0a0a0a] border border-cyan-500/20 rounded-lg shadow-2xl overflow-hidden z-50">
        <div className="p-2 border-b border-white/[0.06]">
          <p className="text-xs text-white/70 px-2" data-testid="header-search-in-call-hint">
            {searchQuery.trim().length < MIN_IN_CALL_QUERY_CHARS
              ? `Type at least ${MIN_IN_CALL_QUERY_CHARS} characters to search this call`
              : `Press Enter to search in this call for "${searchQuery}"`}
          </p>
        </div>
        <div className="py-1">
          <button
            type="submit"
            data-testid="header-search-in-call"
            className="w-full flex items-start gap-3 px-3 py-2.5 rounded-lg text-sm text-left text-white/70 hover:text-white hover:bg-white/[0.04] transition-colors"
          >
            <Search className="w-4 h-4 text-cyan-400 mt-0.5" />
            <span className="font-medium text-white/80">Search Databases for "{searchQuery}"</span>
          </button>
        </div>
      </div>
    );
  }
  return (
    <div className="absolute right-0 top-full mt-1 w-80 bg-[#0a0a0a] border border-cyan-500/20 rounded-lg shadow-2xl overflow-hidden z-50">
      <div className="p-2 border-b border-white/[0.06]">
        <p className="text-xs text-white/70 px-2">Press Enter to search for "{searchQuery}"</p>
      </div>
      {noticeRow}
      <div className="py-1">
        <SearchSuggestion
          icon={Search}
          label={`Search all for "${searchQuery}"`}
          href={`/search?q=${encodeURIComponent(searchQuery)}`}
          onClick={onSelect}
          newTab={newTab}
        />
        <SearchSuggestion
          icon={Phone}
          label={`Search calls for "${searchQuery}"`}
          href={`/search?type=calls&q=${encodeURIComponent(searchQuery)}`}
          onClick={onSelect}
          newTab={newTab}
        />
        <SearchSuggestion
          icon={FileText}
          label={`Search reports for "${searchQuery}"`}
          href={`/search?type=reports&q=${encodeURIComponent(searchQuery)}`}
          onClick={onSelect}
          newTab={newTab}
        />
        <SearchSuggestion
          icon={Mic}
          label={`Search recordings for "${searchQuery}"`}
          href={`/search?type=recordings&q=${encodeURIComponent(searchQuery)}`}
          onClick={onSelect}
          newTab={newTab}
        />
      </div>
    </div>
  );
}

// ─── Search Suggestion ─────────────────────────────────────────────────────

interface SearchSuggestionProps {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  href: string;
  onClick?: () => void;
  /** Open in a new tab (the call page before it is joined). */
  newTab?: boolean;
}

function SearchSuggestion({ icon: Icon, label, href, onClick, newTab = false }: SearchSuggestionProps) {
  return (
    <Link
      href={href}
      onClick={onClick}
      {...(newTab ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
      className="flex items-center gap-3 px-3 py-2.5 mx-1.5 rounded-lg hover:bg-white/[0.04] transition-all duration-200 group"
    >
      <div className="p-1.5 rounded-md bg-cyan-500/10 group-hover:scale-105 transition-all duration-200">
        <Icon className="w-4 h-4 text-cyan-400" />
      </div>
      <span className="text-sm text-white/60 group-hover:text-cyan-400 transition-colors truncate">
        {label}
      </span>
    </Link>
  );
}

// ─── Developer Nav Link ────────────────────────────────────────────────────

interface DeveloperNavLinkProps {
  href: string;
  icon: React.ComponentType<{ className?: string }>;
  children: React.ReactNode;
}

function DeveloperNavLink({ href, icon: Icon, children }: DeveloperNavLinkProps) {
  return (
    <Link
      href={href}
      className="flex items-center gap-2 px-3 py-2 text-sm text-slate-300 hover:text-white hover:bg-violet-900/30 rounded-lg transition"
    >
      <Icon className="w-4 h-4 text-violet-400" />
      <span>{children}</span>
    </Link>
  );
}

// ============================================================================
// Utility Functions
// ============================================================================

/**
 * Format duration in seconds to MM:SS or HH:MM:SS
 */
function formatDuration(seconds: number): string {
  const hrs = Math.floor(seconds / 3600);
  const mins = Math.floor((seconds % 3600) / 60);
  const secs = seconds % 60;

  if (hrs > 0) {
    return `${hrs}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  }

  return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
}

// ============================================================================
// Exports
// ============================================================================

export default UnifiedHeader;
